import { useEffect, useState } from 'react';

import { Subscription } from '@rails/actioncable';

import { useActionCable } from 'components/ActionCableProvider';

type UseWebsocketSubscriptionOptions<T> = {
  channel: string;
  params?: Record<string, unknown>;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReceived?: (data: T) => void;
  onRejected?: () => void;
  enabled?: boolean;
};

type UseWebsocketSubscriptionReturn = {
  subscription: Subscription | null;
  isConnected: boolean;
};

export const useWebsocketSubscription = <T,>({
  channel,
  params = {},
  onConnected,
  onDisconnected,
  onReceived,
  onRejected,
  enabled = true,
}: UseWebsocketSubscriptionOptions<T>): UseWebsocketSubscriptionReturn => {
  const { consumer } = useActionCable();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!consumer || !enabled) {
      return;
    }

    const sub = consumer.subscriptions.create(
      {
        channel,
        ...params,
      },
      {
        connected: () => {
          setIsConnected(true);
          onConnected?.();
        },
        disconnected: () => {
          setIsConnected(false);
          onDisconnected?.();
        },
        received: (data: T) => {
          onReceived?.(data);
        },
        rejected: () => {
          onRejected?.();
        },
      }
    );

    setSubscription(sub);

    return () => {
      sub.unsubscribe();
      setSubscription(null);
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumer, channel, enabled]);

  return {
    subscription,
    isConnected,
  };
};
